import { useEffect, useState } from "react"
import type { SuccessFetch } from "../../../../../../types"
import type { ProductList } from "../../types"
import { useApi } from "../../../../../../utils"

interface FilteredParams {
  page?: number
  keyword?: string
  limit?: number
  group_id?: string
  sort?: string
  buy?: boolean;
  sell?: boolean;
  inv?: boolean;
  build?: boolean;
  yearmonth?: string
  location_id?: string
}
interface DataType {
  items: ProductList[]
  pagination_data: {
    total: number
    page: number
    size: number
    pages: number
  }
}
export function useList({ page, keyword, limit, group_id, buy, sell, inv, build, sort, yearmonth, location_id}: FilteredParams) {
  const defaultValue = {
    items: [],
    pagination_data: {
      page: 0,
      pages: 0,
      size: 0,
      total: 0
    }
  }

  const api = useApi()
  const [data, setData] = useState<DataType>(defaultValue)
  const [isLoading, setLoading] = useState(true)

  const params: { [key: string]: number | string | boolean | undefined } = {};
  if (page) params.page = page;
  if (keyword) params.keyword = keyword;
  if (limit) params.limit = limit;
  if (group_id) params.group_id = group_id;
  if (buy) params.buy = buy;
  if (sell) params.sell = sell;
  if (inv) params.inv = inv;
  if (build) params.build = build;
  if (sort) params.sort = sort;
  if (yearmonth) params.yearmonth = yearmonth;
  if (location_id) params.location_id = location_id;

  const fetchingData = () => api.get("/product/all", { params })
  const fetchData = () => {
    setLoading(true)

    fetchingData()
      .then((res: SuccessFetch<{ payload: DataType }>) => setData(res.data.payload))
      .catch(() => setData(defaultValue))
      .finally(() => setLoading(false))
  }
  const refetch = () => fetchData()

  useEffect(() => {
    refetch()

    return () => {
      setData(defaultValue)
      setLoading(true)
    }

    // eslint-disable-next-line
  }, [])
  useEffect(() => {
    fetchData()

    return () => {
      setData(defaultValue)
      setLoading(true)
    }

    // eslint-disable-next-line
  }, [page, group_id, limit, sort, buy, sell, inv, build, keyword, yearmonth, location_id])

  return { data, isLoading, refetch }
}


type DataTypeKey = "register" | "transfer" | "receive" | "count" | "adjusment" | "build" | "rquest"

export type FilterAdjusment = {
  start_date?: string;
  end_date?: string;
  location_id?: string;
  approved?: string;
  source?: string;
  jumlah_item?: number;
};



const apiConfig: Record<DataTypeKey, {
  url: string;
  buildParams: (filters: any) => Record<string, any>;
}> = {
  adjusment: {
    url: "/invadjustment/data_inv_adjustment_export",
    buildParams: (filters) => ({
      start_date: filters.start_date,
      end_date: filters.end_date,
      location_id: Number(filters.location_id),
      approved: Number(filters.approved),
      source: Number(filters.source),
      jumlah_item: Number(filters.jumlah_item)
    }),
  },
  count: {
    url: "/invcount/register_count_export",
    buildParams: (filters) => ({
      start_date: filters.start_date,
      end_date: filters.end_date,
      location_id: Number(filters.location_id),
      count_status: Number(filters.count_status),
      jumlah_item: Number(filters.jumlah_item)
    }),
  },
  build: {
    url: "/list_build_item/builditems_export",
    buildParams: (filters) => ({
      date_from: filters.start_date,
      date_end: filters.end_date,
      approve_status: Number(filters.approve_status),
      location_id: Number(filters.location_id),
      group_id: Number(filters.group_id),
      jumlah_item: Number(filters.jumlah_item)
    }),
  },

  receive: {
    url: "/received/register_export",
    buildParams: (filters) => ({
      start_date: filters.start_date,
      end_date: filters.end_date,
      approval_status: Number(filters.approval_status),
      received_status: Number(filters.received_status),
      from_location_id: Number(filters.from_location_id),
      to_location_id: Number(filters.to_location_id),
      jumlah_item: Number(filters.jumlah_item)
    }),
  },

  transfer: {
    url: "/transfer/register_export",
    buildParams: (filters) => ({
      start_date: filters.start_date,
      end_date: filters.end_date,
      approval_status: Number(filters.approval_status),
      transfer_status: Number(filters.transfer_status),
      from_location_id: Number(filters.from_location_id),
      to_location_id: Number(filters.to_location_id),
      jumlah_item: Number(filters.jumlah_item)
    }),
  },
  register: {
    url: "/product/allproduct",
    buildParams: (filters) => ({
      build: filters.build,
      buy: filters.buy,
      group_id: filters.group_id,
      sell: filters.sell,
      inv: filters.inv,
      jumlah_item: Number(filters.jumlah_item)
    }),
  },
  rquest: {
    url: "/requestform/getproduct_export",
    buildParams: (filters) => ({
      buy: filters.buy,
      inventory: filters.inventory,
      isbuild: filters.isbuild,
      jumlah_item: Number(filters.jumlah_item)
    }),
  },
};


export function useListExportNew<T, F = any>(type: DataTypeKey, filters: F) {
  const api = useApi();
  const [data, setData] = useState<{ items: T[]; total_record: number }>({
    items: [],
    total_record: 0,
  });
  const [isLoading, setLoading] = useState(true);

  const fetchData = () => {
    setLoading(true);
    const { url, buildParams } = apiConfig[type];
    const params = buildParams(filters);

    api
      .get(url, { params })
      .then((res: SuccessFetch<{ payload: typeof data }>) => setData(res.data.payload))
      .catch(() => setData({ items: [], total_record: 0 }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    return () => {
      setData({ items: [], total_record: 0 });
      setLoading(true);
    };

    // eslint-disable-next-line
  }, [type, JSON.stringify(filters)]);

  return { data, isLoading, refetch: fetchData };
}

